import { useState, useEffect, ReactNode } from 'react';
import { LegacyButton } from '../../navigation/LegacyButton';
import { Icon } from '../../display/Icon';
import styles from './Accordion.module.scss';

type Props = {
  /**
   * Title to show at all times
   */
  title: ReactNode;

  /**
   * Content to show when open
   */
  children: ReactNode;

  /**
   * An optional URL presented as a button-styled link to open the full content.
   * Opens in a new tab.
   */
  href?: string;

  /**
   * Whether the accordion should be open when first shown
   */
  isInitiallyOpen?: boolean;

  /**
   * Enables `Accordion` to act as a semi-controlled component. A parent will be
   * able to manage the open state. Clicking/toggling the component will still
   * take precedence until this prop changes again.
   */
  isOpen?: boolean;

  /**
   * Called when the accordion opens and closes.
   */
  onToggle?: (state: { isOpen: boolean }) => void;

  /**
   * Determines which side the `Accordion` toggle should be on.
   * @default 'left'
   */
  toggleSide?: 'left' | 'right';
};

/**
 * A styled `<details>` and `<summary>` component that opens and closes on
 * click.
 *
 * ---
 *
 * **The suggested gap size between accordion elements is**
 * `var(--zds-space-24)`, which is 24px or 1.5rem.
 */
export const Accordion = ({
  children,
  href,
  isInitiallyOpen = false,
  onToggle,
  title,
  toggleSide = 'left',
  isOpen,
}: Props) => {
  const [openState, setOpenState] = useState(Boolean(isInitiallyOpen));

  // If the component has a defined `isOpen`, it's a controlled component and we
  // should sync the props to our internal state. The HTML element `<details>`
  // has its own internal state controlled by the attribute/property named
  // `open`, a boolean property. The DOM automatically enables/disables this
  // property when you click, but React doesn't follow this two-way model, so
  // the `isOpen` prop can be different from the internal state.
  useEffect(() => {
    if (isOpen !== undefined) {
      setOpenState(isOpen);
    }
  }, [isOpen]);

  return (
    <details
      data-toggle-side={toggleSide}
      className={styles.root}
      open={openState}
      data-zds
    >
      <summary
        data-toggle-side={toggleSide}
        className={styles.summary}
        onClick={(event) => {
          event.preventDefault();
          if (onToggle) {
            onToggle({ isOpen: !openState });
          }
          // We prevent internal state management to consider multiple
          // controlled Accordions. In this case, an Accordion might toggle
          // states and then re-render based on `isOpen`. With `useEffect`,
          // there will be slight jank when the the effect fires, because the
          // browser has already re-painted.
          if (isOpen === undefined) {
            setOpenState(!openState);
          }
        }}
        data-zds
      >
        <span data-toggle-side={toggleSide} className={styles.arrow} data-zds>
          <Icon
            canAcceptPointerEvents={false}
            name={openState ? 'arrowSmallUp' : 'arrowSmallDown'}
            size={24}
          />
        </span>
        {title}
      </summary>

      <div data-toggle-side={toggleSide} className={styles.content} data-zds>
        {children}
      </div>

      {href && (
        <div className={styles.fullPageButton} data-zds>
          <LegacyButton
            color="tertiary"
            href={href}
            iconBefore="arrowOffsite"
            isFullWidth={true}
            size="medium"
            target="_blank"
          >
            Open full page
          </LegacyButton>
        </div>
      )}
    </details>
  );
};
